import React, { useState, useEffect } from 'react';
import { Button } from '../shadComponents/ui/button';
import GalleryUserEditModal from './GalleryUserEditModal';
import GalleryUserCreateModal from './GalleryUserCreateModal';
import useAuthenticatedApi from '../Authentication/components/hooks/useAuthenticatedApi';
import { Badge } from '../shadComponents/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../shadComponents/ui/table';
import { Loader2 } from 'lucide-react';
import PageShell from '@/src/Common/components/PageShell';

interface GalleryUser {
    id: number;
    firstName: string;
    lastName: string;
    email: string | null;
    phoneNumber: string | null;
    igHandle: string | null;
    facialRecognitionOptedIn: boolean;
    needsRollupCommunication: boolean;
}

const GalleryUserList: React.FC = () => {
    const [users, setUsers] = useState<GalleryUser[]>([]);
    const [selectedUser, setSelectedUser] = useState<GalleryUser | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const authenticatedApi = useAuthenticatedApi();
    const [isPushingRollups, setIsPushingRollups] = useState(false);

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await authenticatedApi.get('/gallery-users');
            const data = response.data;
            setUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleDelete = async (userId: number) => {
        if (!window.confirm('Are you sure you want to delete this user?'))
            return;

        try {
            await authenticatedApi.delete(`/gallery-users/${userId}`);
            fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    };

    const pushRollupCommunication = async (userId: number) => {
        try {
            await authenticatedApi.post(`/gallery-users/${userId}/push-rollup`);
            fetchUsers();
        } catch (error) {
            console.error('Error pushing rollup communication:', error);
        }
    };

    const pushRollupCommunications = async () => {
        if (isPushingRollups) return;
        try {
            setIsPushingRollups(true);
            await authenticatedApi.post('/gallery-users/push-rollups');
            fetchUsers();
        } catch (error) {
            console.error('Error pushing rollup communications:', error);
        } finally {
            setIsPushingRollups(false);
        }
    };

    const actions = (
        <div className="flex items-center gap-2">
            <Button onClick={() => setIsCreateModalOpen(true)}>
                Add User
            </Button>
            <Button variant="outline" onClick={() => pushRollupCommunications()}>
                {isPushingRollups ? <Loader2 className="size-4 animate-spin" /> : 'Push Rollups'}
            </Button>
        </div>
    );

    return (
        <PageShell
            title="Gallery Users"
            breadcrumbs={[{ label: 'Admin', href: '/admin/gallery-users' }, { label: 'Gallery Users' }]}
            actions={actions}
        >
            <div className="bg-background rounded shadow overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Contact</TableHead>
                            <TableHead>Instagram</TableHead>
                            <TableHead>Face Recognition</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell>
                                    {user.firstName} {user.lastName}
                                </TableCell>
                                <TableCell>
                                    {user.email || user.phoneNumber}
                                </TableCell>
                                <TableCell>
                                    {user.igHandle || '-'}
                                </TableCell>
                                <TableCell>
                                    <Badge variant={user.facialRecognitionOptedIn ? 'default' : 'secondary'}>
                                        {user.facialRecognitionOptedIn
                                            ? 'Yes'
                                            : 'No'}
                                    </Badge>
                                </TableCell>
                                <TableCell className="space-x-2">
                                    {user.needsRollupCommunication && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                pushRollupCommunication(
                                                    user.id,
                                                );
                                            }}
                                        >
                                            Push Rollup
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setSelectedUser(user);
                                            setIsEditModalOpen(true);
                                        }}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => handleDelete(user.id)}
                                    >
                                        Delete
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {selectedUser && (
                <GalleryUserEditModal
                    open={isEditModalOpen}
                    onOpenChange={setIsEditModalOpen}
                    user={selectedUser}
                    onUserUpdated={fetchUsers}
                />
            )}

            <GalleryUserCreateModal
                open={isCreateModalOpen}
                onOpenChange={setIsCreateModalOpen}
                onUserCreated={fetchUsers}
            />
        </PageShell>
    );
};

export default GalleryUserList;
